import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WiziQuizHeader from '../../components/wizi-quiz/WiziQuizHeader';
import WiziQuizNavigator from '../../components/wizi-quiz/WiziQuizNavigator';
import { WiziQuestionState } from '../../utils/wiziQuizStateManager';
import AnswerOptions from '../../components/AnswerOptions';
import { AnswerStatus, MyTestDetail, Paper, PaperQuestion, myTestApi, showTime } from '../../utils/myTestApi';

/**
 * Taking a test the school set. Phase 4, task 4.9 (web), over My_test.php.
 *
 * THE SERVER'S CLOCK. The deadline is fixed by the API when the test starts
 * and arrives as `seconds_left`. The countdown here runs from that, not from
 * the device's idea of the time, and when it reaches zero the page submits.
 * The API refuses late answers whatever this page does; the countdown is
 * there so the student is not surprised.
 *
 * EVERY ANSWER IS SAVED AS IT IS GIVEN, one request per change, in order
 * (a queue, so a slow request cannot land after a newer one). A reload, a
 * crash or a different device resumes from what the server has: pressing
 * Start again on an open attempt returns the same attempt and deadline.
 *
 * NOTHING HERE KNOWS THE ANSWERS. The API sends no key, no right/wrong and
 * no marks during the test; saving answers "saved" and nothing else.
 */

type State = WiziQuestionState;

const initialStates = (qs: PaperQuestion[]): State[] =>
  qs.map(q => ({
    status: q.status,
    user_answer: q.user_answer,
    time_spent: q.time_spent || 0,
    visited: q.status !== 'not_attempted',
  }));

const MyTestTake: React.FC = () => {
  const { id } = useParams();
  const assignmentId = Number(id);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<MyTestDetail | null>(null);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [index, setIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [integerDraft, setIntegerDraft] = useState('');
  const [showImage, setShowImage] = useState(false);

  const deadline = useRef<number>(0);
  const queue = useRef<Promise<any>>(Promise.resolve());
  const shownAt = useRef<number>(Date.now());
  const finished = useRef(false);

  // --- load: an open attempt resumes at once; otherwise the instructions ---
  useEffect(() => {
    myTestApi
      .get(assignmentId)
      .then(r => {
        setDetail(r.result);
        if (r.result.in_progress) begin();
      })
      .catch(e => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const begin = async () => {
    setStarting(true);
    try {
      const r = await myTestApi.start(assignmentId);
      const p = r.result;
      deadline.current = Date.now() + (p.seconds_left || 0) * 1000;
      setSecondsLeft(p.seconds_left || 0);
      setPaper(p);
      const st = initialStates(p.questions);
      // Resume on the first question not yet answered.
      const first = st.findIndex(s => !s.user_answer);
      const at = first === -1 ? 0 : first;
      st[at] = { ...st[at], visited: true };
      setStates(st);
      setIndex(at);
      shownAt.current = Date.now();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  // --- finishing -------------------------------------------------------------
  const finish = useCallback(
    async (auto: boolean) => {
      if (finished.current) return;
      finished.current = true;
      setSubmitting(true);
      try {
        await queue.current; // every answer given first
      } catch {
        /* a failed save is already shown; the server has what it has */
      }
      try {
        await myTestApi.submit(assignmentId);
      } catch {
        // Already closed -- the deadline passed and the server closed it.
        // Either way the result page shows what counted.
      }
      navigate(`/my-tests/${assignmentId}`, { replace: true, state: { justSubmitted: true, auto } });
    },
    [assignmentId, navigate]
  );

  // --- countdown, from the server's deadline --------------------------------
  useEffect(() => {
    if (!paper) return;
    const tick = () => {
      const left = Math.max(0, Math.round((deadline.current - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) finish(true);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [paper, finish]);

  // --- saving ---------------------------------------------------------------
  /** Time on the question being left, added to what it already had. */
  const spentOnCurrent = () => {
    const extra = Math.round((Date.now() - shownAt.current) / 1000);
    shownAt.current = Date.now();
    return extra;
  };

  const save = (i: number, userAnswer: string | null, status: AnswerStatus) => {
    if (!paper || finished.current) return;
    const q = paper.questions[i];
    const time = (states[i]?.time_spent || 0) + (i === index ? spentOnCurrent() : 0);
    setStates(prev => {
      const next = [...prev];
      next[i] = { ...next[i], user_answer: userAnswer, status, time_spent: time, visited: true };
      return next;
    });
    queue.current = queue.current
      .catch(() => undefined)
      .then(() => myTestApi.answer(assignmentId, { question_user_id: q.id, user_answer: userAnswer, status, time_spent: time }))
      .then(() => setSaveError(null))
      .catch((e: any) => {
        setSaveError(`Question ${i + 1} was not saved: ${e.message}`);
        if (/time is up/i.test(e.message)) finish(true);
      });
  };

  const go = (i: number) => {
    if (!paper || i < 0 || i >= paper.questions.length) return;
    // Leaving a question: an integer answer typed but not saved is saved now.
    const cur = paper.questions[index];
    if (cur.question_type === 'integer' && integerDraft.trim() !== '' && integerDraft.trim() !== (states[index]?.user_answer || '')) {
      save(index, integerDraft.trim(), states[index]?.status === 'marked_review' || states[index]?.status === 'answered_marked_review' ? 'answered_marked_review' : 'answered');
    } else {
      // Kept locally; it goes to the server with this question's next save.
      const extra = spentOnCurrent();
      setStates(prev => {
        const next = [...prev];
        next[index] = { ...next[index], time_spent: (next[index]?.time_spent || 0) + extra };
        return next;
      });
    }
    setStates(prev => {
      const next = [...prev];
      next[i] = { ...next[i], visited: true };
      return next;
    });
    setIndex(i);
  };

  useEffect(() => {
    if (paper) setIntegerDraft(states[index]?.user_answer || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, paper]);

  // --- screens --------------------------------------------------------------
  if (error) {
    return (
      <div className="container py-5" style={{ maxWidth: 640 }}>
        <div className="alert alert-warning">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/my-tests')}>
          Back to my tests
        </button>
      </div>
    );
  }

  if (!paper) {
    if (!detail || starting || detail.in_progress) {
      return (
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2">{starting ? 'Opening your test…' : 'Loading…'}</div>
        </div>
      );
    }
    // Instructions, before the clock starts.
    return (
      <div className="container py-5" style={{ maxWidth: 720 }}>
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h3 className="mb-3">{detail.title}</h3>
            <ul className="mb-3">
              <li>
                You have <strong>{detail.duration_minutes} minutes</strong> from when you press Start. The test closes at{' '}
                {showTime(detail.scheduled_end)} whatever happens, so if you start late you get less time.
              </li>
              <li>Each answer is saved as soon as you choose it. If your connection drops, open the test again and continue.</li>
              <li>When the time is up, the test is submitted for you with the answers you gave.</li>
              {detail.max_attempts > 1 && (
                <li>
                  You may take it {detail.max_attempts} times ({detail.attempts_used} used). Your best score counts.
                </li>
              )}
            </ul>
            {detail.instructions && (
              <div className="alert alert-light border">
                <div className="small text-muted mb-1">From your teacher</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{detail.instructions}</div>
              </div>
            )}
            {detail.can_start ? (
              <button className="btn btn-primary btn-lg w-100" onClick={begin}>
                Start now
              </button>
            ) : (
              <div className="alert alert-warning mb-0">{detail.cannot_start_reason || 'This test cannot be started now.'}</div>
            )}
            <button className="btn btn-link w-100 mt-2" onClick={() => navigate('/my-tests')}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = paper.questions[index];
  const st = states[index];
  const review = st.status === 'marked_review' || st.status === 'answered_marked_review';
  const answered = states.filter(s => s.user_answer).length;
  // Same as the custom quiz: the question is an image, the options are just A-D.
  const optionCount = Number(q.content?.option_count) || 4;
  const selectedOption = st.user_answer ? Number(st.user_answer) - 1 : null;

  const choose = (value: string) => save(index, value, review ? 'answered_marked_review' : 'answered');
  const toggleReview = () => {
    const ans = q.question_type === 'integer' ? integerDraft.trim() || null : st.user_answer;
    if (review) save(index, ans, ans ? 'answered' : 'not_attempted');
    else save(index, ans, ans ? 'answered_marked_review' : 'marked_review');
  };
  const clear = () => {
    setIntegerDraft('');
    save(index, null, 'not_attempted');
  };

  return (
    <div className="vh-100 d-flex flex-column">
      <WiziQuizHeader
        candidateName={sessionStorage.getItem('wiziai_display_name') || 'Student'}
        examName={paper.title}
        subjectName=""
        timeRemaining={secondsLeft}
      />

      {saveError && <div className="alert alert-danger rounded-0 mb-0 py-2 small">{saveError}</div>}

      <div className="flex-grow-1 d-flex flex-column flex-lg-row overflow-hidden">
        <div className="flex-grow-1 overflow-auto p-3 p-md-4">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white d-flex justify-content-between">
              <h5 className="mb-0">Question {index + 1} of {paper.questions.length}</h5>
              <span className="small">
                +{q.marks}
                {q.negative_marks ? ` / −${Math.abs(q.negative_marks)}` : ''}
              </span>
            </div>
            <div className="card-body">
              {q.content?.question_img_url ? (
                <div className="text-center">
                  <img
                    src={q.content.question_img_url}
                    alt="Question"
                    className="img-fluid"
                    style={{ maxHeight: window.innerWidth < 768 ? '180px' : '300px', borderRadius: '12px', cursor: 'pointer' }}
                    onClick={() => setShowImage(true)}
                  />
                  <div className="small text-muted mt-1">
                    <i className="fas fa-expand-arrows-alt me-1"></i>
                    Tap the image to view full size
                  </div>
                </div>
              ) : q.content?.question_text ? (
                <div className="mb-3 fs-5" style={{ whiteSpace: 'pre-wrap' }}>{q.content.question_text}</div>
              ) : (
                <div className="alert alert-warning mb-0">This question could not be loaded. Tell your teacher.</div>
              )}

              <div className="mt-4">
                {q.question_type === 'integer' ? (
                  <div>
                    <label htmlFor="integerAnswer" className="form-label fw-bold">
                      Your answer (a number)
                    </label>
                    <div className="d-flex gap-2" style={{ maxWidth: 360 }}>
                      <input
                        id="integerAnswer"
                        type="text"
                        inputMode="decimal"
                        className="form-control form-control-lg"
                        value={integerDraft}
                        maxLength={45}
                        onChange={e => setIntegerDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && integerDraft.trim() !== '') choose(integerDraft.trim());
                        }}
                      />
                      <button className="btn btn-outline-primary" disabled={integerDraft.trim() === ''} onClick={() => choose(integerDraft.trim())}>
                        Save
                      </button>
                    </div>
                    {st.user_answer && <div className="small text-success mt-1">Saved: {st.user_answer}</div>}
                  </div>
                ) : (
                  <AnswerOptions
                    optionCount={optionCount}
                    selectedOption={selectedOption}
                    isSavingAnswer={false}
                    isQuizActive={!submitting}
                    onOptionSelect={i => choose(String(i + 1))}
                  />
                )}
              </div>
            </div>
            <div className="card-footer d-flex flex-wrap gap-2">
              <button className="btn btn-outline-secondary" onClick={clear} disabled={!st.user_answer && q.question_type !== 'integer'}>
                Clear
              </button>
              <button className={`btn ${review ? 'btn-warning' : 'btn-outline-warning'}`} onClick={toggleReview}>
                {review ? 'Marked for review' : 'Mark for review'}
              </button>
              <div className="flex-grow-1" />
              <button className="btn btn-outline-primary" onClick={() => go(index - 1)} disabled={index === 0}>
                Previous
              </button>
              {index < paper.questions.length - 1 ? (
                <button className="btn btn-primary" onClick={() => go(index + 1)}>
                  Next
                </button>
              ) : (
                <button className="btn btn-success" onClick={() => setConfirmSubmit(true)}>
                  Finish
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-start bg-light overflow-auto" style={{ flex: '0 0 300px' }}>
          <WiziQuizNavigator
            questions={paper.questions.map(x => ({ id: x.id, question_order: x.question_order }))}
            questionStates={states}
            currentQuestionIndex={index}
            onQuestionSelect={go}
            onSubmit={() => setConfirmSubmit(true)}
            disabled={submitting}
          />
        </div>
      </div>

      {confirmSubmit && (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Submit your test?</h5>
              </div>
              <div className="modal-body">
                <p className="mb-1">
                  You have answered <strong>{answered}</strong> of {paper.questions.length} questions.
                </p>
                {answered < paper.questions.length && (
                  <p className="text-warning mb-1">{paper.questions.length - answered} are still blank.</p>
                )}
                <p className="mb-0 text-muted small">After submitting you cannot change your answers.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setConfirmSubmit(false)} disabled={submitting}>
                  Keep going
                </button>
                <button className="btn btn-success" onClick={() => finish(false)} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showImage && q.content?.question_img_url && (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.8)' }} onClick={() => setShowImage(false)}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Question {index + 1}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowImage(false)} />
              </div>
              <div className="modal-body text-center" style={{ overflow: 'auto' }}>
                <img src={q.content.question_img_url} alt="Question" className="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      )}
      {submitting && !confirmSubmit && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-4 text-center">Time is up. Submitting your answers…</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTestTake;
